/**
 * SiteSlide — annotated-screenshot layout for the Around the Site chapter.
 *
 * A real (PIL-annotated) screenshot on the left, and a numbered legend on
 * the right whose circle badges mirror the badges baked into the image:
 * sage fill, white number — badge 1 in amber ("start here"), matching the
 * annotate.py spec. `objectFit: contain` so baked-in badges never crop.
 */
import { Box, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { accentColor } from './tourCore';
import type { SiteSlideDef } from './siteSlides';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const frameInRight = keyframes`
  from { opacity: 0; transform: translateX(5vw) scale(0.97); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const frameInLeft = keyframes`
  from { opacity: 0; transform: translateX(-5vw) scale(0.97); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

interface SiteSlideProps {
  slide: SiteSlideDef;
  direction: 1 | -1;
}

export const SiteSlide = ({ slide, direction }: SiteSlideProps) => {
  const theme = useTheme();
  const accent = accentColor(slide.accent, theme);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 2, md: 5 },
        px: { xs: 2.5, md: 7 },
        pt: { xs: 9, md: 8 },
        pb: { xs: 10, md: 8 },
      }}
    >
      {/* Screenshot */}
      <Box
        sx={{
          flex: { xs: '0 1 auto', md: '0 1 64%' },
          width: '100%',
          minWidth: 0,
          animation: `${direction === 1 ? frameInRight : frameInLeft} 0.75s cubic-bezier(0.22, 0.9, 0.3, 1) both`,
          borderRadius: `${theme.border.radius * 3}px`,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
          boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.55)}, inset 0 0 90px ${alpha(accent, 0.08)}`,
          backgroundColor: alpha(theme.palette.common.white, 0.04),
        }}
      >
        <Box
          component="img"
          src={slide.image}
          alt={slide.alt}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            (e.currentTarget.parentElement as HTMLElement).style.display = 'none';
          }}
          sx={{
            display: 'block',
            width: '100%',
            height: { xs: '36vh', md: '66vh' },
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Legend */}
      <Box sx={{ flex: { xs: '0 0 auto', md: '0 1 36%' }, maxWidth: 460, minWidth: 0, width: '100%' }}>
        <Typography
          sx={{
            animation: `${riseIn} 0.6s ease-out both`,
            color: slide.accent === 'night' ? theme.schedule.tentative : alpha(accent, 0.95),
            filter: 'brightness(1.35)',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.68rem', md: '0.78rem' },
            mb: 1,
          }}
        >
          {slide.kicker}
        </Typography>
        <Typography
          component="h1"
          sx={{
            animation: `${riseIn} 0.7s 0.1s ease-out both`,
            color: theme.palette.common.white,
            fontWeight: 800,
            lineHeight: 1.12,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem' },
            mb: slide.intro ? 1.25 : 2.25,
            textWrap: 'balance',
          }}
        >
          {slide.title}
        </Typography>
        {slide.intro && (
          <Typography
            sx={{
              animation: `${riseIn} 0.7s 0.2s ease-out both`,
              color: alpha(theme.palette.common.white, 0.75),
              fontSize: { xs: '0.85rem', md: '0.95rem' },
              lineHeight: 1.55,
              mb: 2,
            }}
          >
            {slide.intro}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {slide.callouts.map((c, i) => (
            <Box
              key={c.n}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                animation: `${riseIn} 0.55s ease-out both`,
                animationDelay: `${0.3 + i * 0.14}s`,
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: c.n === 1 ? theme.schedule.maintenance : theme.palette.primary.main,
                  border: `2px solid ${alpha(theme.palette.common.white, 0.85)}`,
                  color: theme.palette.common.white,
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  boxShadow: `0 3px 10px ${alpha(theme.palette.common.black, 0.4)}`,
                }}
              >
                {c.n}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ color: theme.palette.common.white, fontWeight: 700, fontSize: { xs: '0.85rem', md: '0.95rem' }, lineHeight: 1.3 }}
                >
                  {c.label}
                </Typography>
                <Typography
                  sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: { xs: '0.78rem', md: '0.85rem' }, lineHeight: 1.5 }}
                >
                  {c.detail}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
